import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { typographyUtils } from 'baseline-ui/typography';
import { has } from 'lodash';
import thumbnail1 from 'assets/images/thumbnail.jpeg'
import thumbnail2 from 'assets/images/thumbnail2.jpeg'
import thumbnail3 from 'assets/images/thumbnail3.jpeg'

const VideoPlayer = ({
    videoId = null,          // Now expects full video URL
    autoplay = false,
    showControls = true,
    disableKeyboard = false, // Not applicable for HTML5 video
    fullScreenButton = true, // Handled natively by browser
    loop = false,
    light = true,  
    thumbnail = 0,     // Now controls whether to show thumbnail
    ...props
}) => {
    const [isPlaying, setIsPlaying] = useState(autoplay);
    const thumbnails = [thumbnail1, thumbnail2, thumbnail3]

    
    const handlePlay = () => {
        setIsPlaying(true);
    };

    return (
        <VideoContainer {...props}>
            {!isPlaying && light ? (
                <ThumbnailContainer onClick={handlePlay}>
                    <ThumbnailImage src={thumbnails[thumbnail]} alt="Video thumbnail" />
                    <PlayButton>
                        <PlayIcon viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                        </PlayIcon>
                    </PlayButton>
                </ThumbnailContainer>
            ) : (
                <video
                    controls={showControls}
                    autoPlay={isPlaying || autoplay}
                    loop={loop}
                    playsInline
                    width="100%"
                    height="100%"
                    // disableKeyboard isn't directly applicable in HTML5 video
                >
                    <source src={videoId} type={`video/${videoId.split('.').pop().split('?')[0]}`} />
                    Your browser does not support the video tag.
                </video>
            )}
        </VideoContainer>
    );
};

VideoPlayer.propTypes = {
    videoId: PropTypes.string.isRequired, // Now expects full URL
    autoplay: PropTypes.bool,
    showControls: PropTypes.bool,
    disableKeyboard: PropTypes.bool,      // Kept for consistency but not used
    fullScreenButton: PropTypes.bool,     // Kept for consistency but not used
    light: PropTypes.bool,                // Now controls thumbnail display
    loop: PropTypes.bool,
    thumbnailUrl: PropTypes.string,       // Optional custom thumbnail
    margin: PropTypes.shape({
        bottom: PropTypes.number,
        left: PropTypes.number,
        right: PropTypes.number,
        top: PropTypes.number,
    }),
    padding: PropTypes.shape({
        bottom: PropTypes.number,
        left: PropTypes.number,
        right: PropTypes.number,
        top: PropTypes.number,
    })
};

// (Keep all the same styled components as before)
const VideoContainer = styled.div`
    height: 0;
    position: relative;
    padding-top: 56.25%;
    width: 100%;
    margin-top: ${({margin}) => has(margin, 'top') ? typographyUtils.processSize(margin.top) : 0};
    margin-right: ${({margin}) => has(margin, 'right') ? typographyUtils.processSize(margin.right) : 0};
    margin-bottom: ${({margin}) => has(margin, 'bottom') ? typographyUtils.processSize(margin.bottom) : 0};
    margin-left: ${({margin}) => has(margin, 'left') ? typographyUtils.processSize(margin.left) : 0};
    border: 1px solid black;
    & video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        background-color: #000;
    }
`;

const ThumbnailContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    background-color: #000;
        border: .3px solid black;
`;

const ThumbnailImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const PlayButton = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100px;
    height: 60px;
    background: rgb(255, 255, 255);
    border: 1px solid #000000;
    opacity: 1;
    border-radius: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    animation: vibrate 0.5s ease 1s 2 alternate;
    
    &:hover {
        transform: translate(-50%, -50%) scale(1.1);
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.3);
    }

    @keyframes vibrate {
        0% { transform: translate(-50%, -50%) rotate(0deg); }
        25% { transform: translate(-50%, -50%) rotate(2deg); }
        50% { transform: translate(-50%, -50%) rotate(0deg); }
        75% { transform: translate(-50%, -50%) rotate(-2deg); }
        100% { transform: translate(-50%, -50%) rotate(0deg); }
    }
`;

const PlayIcon = styled.svg`
    width: 40px;
    height: 40px;
    fill: #000000;
    transition: all 0.3s ease;
    
    ${PlayButton}:hover & {
        transform: scale(1.1);
    }
`;

export default VideoPlayer;